import {
    Avatar,
    Box,
    Button,
    HStack,
    Icon,
    IconButton,
    Image,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Stack,
    Text,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    deleteReply,
    toggleBlockProfile,
    toggleLikeReply,
    toggleReportReply,
} from "../../api";
import { colors } from "../../styles";
import { IChatDetail, IReply } from "../../types";
import { formatRelativeTime } from "../../utils";
import MyIcon from "../Icon/MyIcon";
import ConfirmAlert, { useConfirmAlert } from "./ConfirmAlert";
import AnimatedReactionIcon from "./AnimatedReactionIcon";
import { ViewOffIcon } from "@chakra-ui/icons";
import { useChatDeleteLoading } from "./ChatDeleteLoading";
import LikesModal from "../../routes/Chat/LikesModal";

interface ReplyProps {
    slug: string;
    commentId: string;
    reply: IReply;
}

function Reply({ slug, commentId, reply }: ReplyProps) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const toast = useToast();
    const { showConfirm, confirmAlertProps } = useConfirmAlert();
    const deleteLoading = useChatDeleteLoading();
    const [likePulse, setLikePulse] = useState(0);
    const { isOpen: isLikesModalOpen, onOpen: onLikesModalOpen, onClose: onLikesModalClose } = useDisclosure();

    const {
        id,
        profile,
        content,
        likeCount,
        isMine,
        isLiked,
        isReported,
        deletedAt,
        hiddenAt,
        createdAt,
    } = reply;

    const handleGoToProfile = () => {
        navigate(`/chat/profile/${profile.nickname}`);
    };

    const toggleLikeReplyMutation = useMutation({
        mutationFn: () => toggleLikeReply({ slug, commentId, replyId: id }),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ["chat", slug] });
            const previous = queryClient.getQueryData<IChatDetail>(["chat", slug]);

            queryClient.setQueryData<IChatDetail>(["chat", slug], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    comments: old.comments.map((c) =>
                        c.id === commentId
                            ? {
                                ...c,
                                replies: c.replies.map((r) =>
                                    r.id === id
                                        ? {
                                            ...r,
                                            isLiked: !r.isLiked,
                                            likeCount: r.likeCount + (r.isLiked ? -1 : 1),
                                        }
                                        : r
                                ),
                            }
                            : c
                    ),
                };
            });

            return { previous };
        },
        onError: (error, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(["chat", slug], context.previous);
            }
            console.error(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
        },
    });

    const toggleReportReplyMutation = useMutation({
        mutationFn: () => toggleReportReply({ slug, commentId, replyId: id }),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ["chat", slug] });
            const previous = queryClient.getQueryData<IChatDetail>(["chat", slug]);

            queryClient.setQueryData<IChatDetail>(["chat", slug], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    comments: old.comments.map((c) =>
                        c.id === commentId
                            ? {
                                ...c,
                                replies: c.replies.map((r) =>
                                    r.id === id ? { ...r, isReported: !r.isReported } : r
                                ),
                            }
                            : c
                    ),
                };
            });

            return { previous };
        },
        onError: (error, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(["chat", slug], context.previous);
            }
            console.error(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
        },
    });

    const deleteReplyMutation = useMutation({
        mutationFn: () => deleteReply({ slug, commentId, replyId: id }),
        onSuccess: () => {
            confirmAlertProps.onClose();
            deleteLoading?.done();
        },
        onError: (error) => {
            console.error(error);
            deleteLoading?.error();
            toast({
                title: "답글 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const toggleBlockProfileMutation = useMutation({
        mutationFn: () => toggleBlockProfile({ nickname: profile.nickname }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
            queryClient.invalidateQueries({ queryKey: ["chats"] });
        },
        onError: (error) => {
            console.error(error);
        },
    });

    return (
        <Stack
            width={"100%"}
            gap={3}
            paddingTop={5}
            paddingLeft={{
                base: 5,
                sm: 5,
                md: 5,
                lg: 10,
                xl: 10,
            }}
            borderLeft={`0.5px solid ${colors.g4}`}
        >
            <HStack width={"100%"} justifyContent={"space-between"} alignItems={"flex-start"}>
                <Box
                    onClick={handleGoToProfile}
                    cursor={"pointer"}
                    flex={1}
                    minWidth={0}
                >
                    <HStack gap={2} alignItems={"center"}>
                        {profile.avatar ?
                            <Image
                                src={profile.avatar}
                                alt={`${profile.nickname} 프로필 이미지`}
                                width={8}
                                height={8}
                                borderRadius={"full"}
                                objectFit={"cover"}
                                flexShrink={0}
                            />
                            :
                            <Image 
                                src={"/png/profile.png"}
                                alt="프로필 이미지"
                                width={8}
                                height={8}
                                borderRadius="full"
                                objectFit="cover"
                                flexShrink={0}
                            />
                        }
                        <HStack gap={2} alignItems={"center"}>
                            <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                {profile.nickname}
                            </Text>
                            <Text fontSize={12} fontWeight={500} color={colors.g3}>
                                {formatRelativeTime(createdAt)}
                            </Text>
                        </HStack>
                    </HStack>
                </Box>

                {isMine ?
                    (!deletedAt ? 
                        <Button
                            variant={"ghost"}
                            color={colors.g2}
                            flexShrink={0}
                            onClick={(e) => {
                                e.stopPropagation();
                                showConfirm({
                                    message: "이 답글을 삭제하시겠습니까?",
                                    confirmLabel: "삭제",
                                    closeOnConfirm: false,
                                    onConfirm: () => {
                                        deleteLoading?.start();
                                        deleteReplyMutation.mutate();
                                    },
                                });
                            }}
                            disabled={deleteReplyMutation.isPending}
                        >
                            <MyIcon src={"/svg/delete-g1.svg"} alt={"삭제"} width={16} height={16} />
                        </Button>
                    : 
                        null
                    )
                    :
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            aria-label='Options'
                            icon={<MyIcon src={"/svg/more-g1.svg"} alt={"더보기"} width={16} height={16} />}
                            variant='ghost'
                        />
                        <MenuList>
                            <MenuItem
                                onClick={() =>
                                    showConfirm({
                                        message: isReported
                                            ? "신고를 취소하겠습니까?"
                                            : "이 답글을 신고하겠습니까?",
                                        confirmLabel: isReported ? "신고 취소" : "신고",
                                        onConfirm: () => toggleReportReplyMutation.mutate(),
                                    })
                                }
                                disabled={toggleReportReplyMutation.isPending}
                            >
                                <Text color={colors.red}>
                                    {isReported ? "신고 취소" : "신고"}
                                </Text>
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    showConfirm({
                                        message: "이 사용자를 차단하겠습니까?",
                                        confirmLabel: "차단",
                                        onConfirm: () => toggleBlockProfileMutation.mutate(),
                                    })
                                }
                                disabled={toggleBlockProfileMutation.isPending}
                            >
                                사용자 차단
                            </MenuItem>
                        </MenuList>
                    </Menu>
                }
            </HStack>

            <Stack width={"100%"} paddingY={2}>
                {deletedAt ? (
                    <HStack width={"100%"} justifyContent={"flex-start"} alignItems={"center"}>
                        <Text fontSize={16} fontWeight={400} color={colors.g4} whiteSpace={"pre-wrap"}>
                            삭제된 답글입니다.
                        </Text>
                    </HStack>
                ) : 
                hiddenAt ? (
                    <HStack width={"100%"} justifyContent={"flex-start"} alignItems={"center"}>
                        <Icon as={ViewOffIcon} color={colors.g4} width={5} height={5} />
                        <Text fontSize={16} fontWeight={400} color={colors.g4} whiteSpace={"pre-wrap"}>
                            관리자에 의해 가려진 답글입니다.
                        </Text>
                    </HStack>
                ) : (
                    <Text fontSize={16} fontWeight={400} color={colors.black} whiteSpace={"pre-wrap"}>
                        {content}
                    </Text>
                )}
            </Stack>

            <HStack gap={5} alignItems={"center"}>
                <HStack
                    gap={1}
                    alignItems={"center"}
                    color={isLiked ? colors.red : colors.g2}
                >
                    <Box
                        as={"button"}
                        type={"button"}
                        display="flex"
                        alignItems="center"
                        onClick={() => {
                            setLikePulse((prev) => prev + 1);
                            toggleLikeReplyMutation.mutate();
                        }}
                        disabled={toggleLikeReplyMutation.isPending}
                    >
                        <AnimatedReactionIcon
                            src={isLiked ? "/svg/heart-filled.svg" : "/svg/heart.svg"}
                            alt={"좋아요"}
                            pulseKey={likePulse}
                        />
                    </Box>
                    <Text
                        as={"button"}
                        type={"button"}
                        fontSize={15}
                        fontWeight={500}
                        onClick={onLikesModalOpen}
                    >
                        {likeCount}
                    </Text>
                </HStack>
            </HStack>

            <LikesModal
                isOpen={isLikesModalOpen}
                onClose={onLikesModalClose}
                targetType="reply"
                targetId={id}
            />

            <ConfirmAlert
                {...confirmAlertProps}
                isLoading={
                    deleteReplyMutation.isPending ||
                    toggleReportReplyMutation.isPending ||
                    toggleBlockProfileMutation.isPending
                }
            />
        </Stack>
    );
}

export default React.memo(Reply);
